import {
  Button,
  SelectOptionType,
  addToast,
  Select,
  TextInput,
} from '@octano/global-ui';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Form, Row } from 'reactstrap';

import { HTTP_STATUS_CODE } from '../../../../api/request';
import {
  createPetition,
  getReintegroPrograms,
} from '../../../../api/requests/petitions';
import { useBase } from '../../../../components/base/BaseProvider';
import { useUserState } from '../../../../hooks/useUserState';
import { useFetch } from '../../../../components/contexts/FetchContext';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  PaginationQuery,
  PaginationRequestType,
} from '../../../../types/paginationRequestType';
import {
  CreatePetitionData,
  PetitionData,
  PetitionInf,
  PetitionPaymentTypeId,
  PetitionTypes,
} from '../../../../types/petitions';
import { TariffFormatter } from '../../../../utils/currency';
import PetitionFormDefault from './PetitionFormDefault';
import PetitionFormProficiency from './PetitionFormProficiency';
import PetitionFormReplaceCourseGrade from './PetitionFormReplaceCourseGrade';
import PetitionFormReplaceEvaluationGrade from './PetitionFormReplaceEvaluationGrade';
import PetitionFormSubjectAssignment from './PetitionFormSubjectAssignment';
import PetitionFormValidation from './PetitionFormValidation';
import PetitionFormCancelCourse from './PetitionFormCancelCourse';
import PetitionFormCancelSemester from './PetitionFormCancelSemester';
import PetitionFormFreezeSemester from './PetitionFormFreezeSemester';
import PetitionFormFreezeCredits from './PetitionFormFreezeCredits';
import PetitionFormReintegro from './PetitionFormReintegro';

export interface CreatePetitionFormProps extends CreatePetitionData {
  close: () => void;
  error?: string;
  loading: boolean;
}
export default function CreatePetitionForm({
  types,
  cellPhone,
  email,
  periodName,
  replaceEvaluationGrades,
  replaceCourseGrades,
  proficiencyCourses,
  subjectAssignmentCourses,
  activeSections,
  isCourseCancellationActive,
  isSemesterCancellationActive,
  credits,
  close,
  error,
  loading,
}: CreatePetitionFormProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const history = useHistory();
  const { userData } = useUserState();

  const { careers } = useBase();
  const careerOptions = useMemo<SelectOptionType[]>(
    () =>
      careers.map((c) => ({
        value: c.studyPlanEnrollmentId,
        label: c.name,
      })),
    [careers],
  );

  const methods = useForm<PetitionData>({
    mode: 'onChange',
    defaultValues: {
      studyPlanEnrollment:
        careerOptions.length === 1 ? careerOptions[0] : undefined,
      type: undefined,
      periodName: periodName,
    },
  });
  const { handleSubmit, reset, watch, control, setValue } = methods;

  const typeOptions = useMemo<SelectOptionType[]>(() => {
    const isNonRegular = userData?.isRegular === false;
    const filteredTypes = isNonRegular
      ? types.filter((t) => t.id === PetitionTypes.REINTEGRO)
      : types;

    return filteredTypes.map((t) => ({
      value: t.id,
      label: t.name,
      paymentRequired: t.paymentRequired,
      disabled:
        (t.id === PetitionTypes.CANCEL_COURSE && !isCourseCancellationActive) ||
        (t.id === PetitionTypes.CANCEL_SEMESTER &&
          !isSemesterCancellationActive),
    }));
  }, [
    types,
    isCourseCancellationActive,
    isSemesterCancellationActive,
    userData,
  ]);

  const text = useMemo(() => {
    const prefix = 'modal';
    return {
      cancel: t(`${prefix}.newPetition.cancel`),
      save: t(`${prefix}.newPetition.save`),
      message: {
        error: t(`${prefix}.newPetition.message.error`),
        success: t(`${prefix}.newPetition.message.success`),
        completed: t(`${prefix}.newPetition.message.completed`),
      },
      type: t(`${prefix}.form.type`),
      payment: t(`${prefix}.form.payment`),
      validation: {
        select: t(`${prefix}.form.validation.select`),
        cancelSection: t(`${prefix}.form.validation.cancelSection`),
        cancelSemester: t(`${prefix}.form.validation.cancelSemester`),
      },
    };
  }, [t]);

  const { updateQuery } = useFetch<
    PaginationQuery,
    PaginationRequestType<PetitionInf>
  >();
  const [loadingSave, setLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [maxCreditsToFreeze, setMaxCreditsToFreeze] = useState<number>();
  const [reintegroProgramOptions, setReintegroProgramOptions] = useState<
    SelectOptionType[]
  >([]);

  const handleLoadingChange = (loading: boolean) => {
    setIsFileLoading(loading);
  };

  const clearForm = useCallback(() => {
    reset({
      studyPlanEnrollment: undefined,
      cellPhone,
      email,
    });
  }, [reset, cellPhone, email]);

  const onSubmit = useCallback(
    async (data: PetitionData) => {
      setLoading(true);

      const {
        cellPhone,
        email,
        docs,
        type,
        title,
        studyPlanEnrollment,
        description,
        replaceEvaluationGrade,
        studentTestGradeId,
        studentFinalGradeId,
        section,
        grade,
        course,
        cancelSection,
        creditsToFreeze,
        periodName,
        reintegroProgram,
      } = data;

      let freezePetitionData;
      if (
        type.value === PetitionTypes.FREEZE_SEMESTER ||
        type.value === PetitionTypes.FREEZE_CREDITS
      ) {
        const selectedCredits = credits.find(
          (credit) =>
            credit.studyPlanEnrollmentId === studyPlanEnrollment.value,
        );
        if (selectedCredits) {
          const maxCreditsToFreeze =
            selectedCredits.contractedCredits - selectedCredits.enrolledCredits;

          freezePetitionData = {
            freezeSemester: type.value === PetitionTypes.FREEZE_SEMESTER,
            periodName,
            contractedCredits: selectedCredits.contractedCredits,
            enrolledCredits: selectedCredits.enrolledCredits,
            maxCreditsToFreeze,
            creditsToFreeze:
              type.value === PetitionTypes.FREEZE_SEMESTER
                ? selectedCredits.contractedCredits
                : creditsToFreeze,
          };
        }
      }

      const petitionData: any = {
        email,
        title,
        description,
        docIds: docs?.map((doc) => Number(doc.id)),
        phone: cellPhone,
        typeId: type.value,
        studyPlanEnrollmentId: studyPlanEnrollment.value,
        replaceEvaluationGrade: replaceEvaluationGrade?.label,
        studentTestGradeId,
        studentFinalGradeId,
        sectionId: section?.value,
        grade,
        courseId: course?.value,
        cancelSectionId: cancelSection?.value,
        targetStudyPlanEnrollmentId: reintegroProgram?.value,
        freezePetitionData,
        /**
         * No se considera aun, porque el precio
         * se calcula a la hora de pagar
         */
        // payment: payment?.value,
      };

      const { error, status } = await createPetition(petitionData);

      if (status === HTTP_STATUS_CODE.CREATED) {
        const message =
          type.value === PetitionTypes.CANCEL_COURSE ||
          type.value === PetitionTypes.CANCEL_SEMESTER
            ? text.message.completed
            : text.message.success;
        addToast({
          icon: 'success',
          color: 'success',
          text: message,
        });
        close();
        clearForm();
        updateQuery({ items_per_page: 10, page: 0 });
      } else if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: text.message.error,
        });
      }
      setLoading(false);
    },
    [close, updateQuery, text, clearForm, setLoading, credits],
  );

  const [selectedType, selectedStudyPlanEnrollment, selectedCourse] = watch([
    'type',
    'studyPlanEnrollment',
    'course',
  ]);

  // Redirect to dedicated page for Credit Reliquidation
  useEffect(() => {
    if (selectedType?.value === PetitionTypes.CREDIT_RELIQUIDATION) {
      close();
      history.push('/petitions/credit-reliquidation');
    }
  }, [selectedType, history, close]);

  useEffect(() => {
    if (selectedType) {
      setValue('replaceEvaluationGrade', undefined);
      setValue('studentTestGradeId', undefined);
      setValue('studentFinalGradeId', undefined);
      setValue('grade', undefined);
      setValue('section', undefined);
      setValue('course', undefined);
      setValue('cancelSection', undefined);
      setValue('reintegroProgram', undefined);
    }
  }, [selectedType, setValue]);

  // Fetch reintegro programs when REINTEGRO type is selected
  useEffect(() => {
    if (selectedType?.value === PetitionTypes.REINTEGRO) {
      getReintegroPrograms().then(({ data }) => {
        if (data) {
          setReintegroProgramOptions(
            data.map((p) => ({ value: p.id, label: p.programName })),
          );
        }
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedType && selectedStudyPlanEnrollment && credits.length > 0) {
      const selectedCredits = credits.find(
        (credit) =>
          credit.studyPlanEnrollmentId === selectedStudyPlanEnrollment.value,
      );

      if (selectedCredits) {
        setValue('contractedCredits', selectedCredits.contractedCredits);
        if (selectedType.value === PetitionTypes.FREEZE_SEMESTER) {
          setValue(
            'semesterEnrolledCredits',
            selectedCredits.enrolledCredits,
            {},
          );
          setValue(
            'semesterCreditsToFreeze',
            selectedCredits.contractedCredits,
          );
        }
        if (selectedType.value === PetitionTypes.FREEZE_CREDITS) {
          const maxCredits =
            selectedCredits.contractedCredits - selectedCredits.enrolledCredits;
          setMaxCreditsToFreeze(maxCredits);
          setValue('enrolledCredits', selectedCredits.enrolledCredits);
          setValue('maxCreditsToFreeze', maxCredits);
          setValue('creditsToFreeze', undefined);
        }
      }
    }
  }, [selectedType, credits, setValue, selectedStudyPlanEnrollment]);

  useMemo(() => {
    const selectedCurrentType = types.find((e) => e.id === selectedType?.value);

    if (selectedCurrentType?.paymentRequired) {
      if (
        selectedCurrentType.paymentType === PetitionPaymentTypeId.BY_CREDIT &&
        selectedCourse?.value &&
        selectedStudyPlanEnrollment?.value
      ) {
        const courses = subjectAssignmentCourses.find(
          (e) => e.studyPlanEnrollmentId === selectedStudyPlanEnrollment.value,
        );
        const selectCourse = courses?.courses?.find(
          (e) => e.id === selectedCourse.value,
        );

        setValue(
          'payment',
          String(selectCourse?.paymentAmount ?? 'No asignado'),
        );
        return;
      }

      if (selectedCurrentType.paymentType === PetitionPaymentTypeId.BY_FIXED) {
        setValue(
          'payment',
          String(selectedCurrentType?.paymentAmount ?? 'No asignado'),
        );
        return;
      }

      // Fallback: paymentRequired but no paymentType — use paymentAmount directly
      if (selectedCurrentType.paymentAmount != null) {
        setValue('payment', String(selectedCurrentType.paymentAmount));
        return;
      }
    }

    setValue('payment', undefined);
  }, [
    selectedType,
    selectedStudyPlanEnrollment,
    selectedCourse,
    types,
    subjectAssignmentCourses,
    setValue,
  ]);

  if (error) {
    return <DisplayError insideCard loadingAction={loading} body={error} />;
  }
  if (loading) {
    return <Loading />;
  }

  const showCancellationCourseWarning =
    selectedType?.value === PetitionTypes.CANCEL_COURSE &&
    !isCourseCancellationActive;
  const showCancellationSemesterWarning =
    selectedType?.value === PetitionTypes.CANCEL_SEMESTER &&
    !isSemesterCancellationActive;

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mt-4 mb-1">
          <Col md={selectedType && selectedType.paymentRequired ? 6 : 12}>
            <Select
              label={text.type}
              name="type"
              control={control}
              options={typeOptions}
              rules={{ required: text.validation.select }}
              isClearable={false}
            />
          </Col>

          {showCancellationCourseWarning && (
            <Col md={12} className="text-danger mb-3">
              {text.validation.cancelSection}
            </Col>
          )}
          {showCancellationSemesterWarning && (
            <Col md={12} className="text-danger mb-3">
              {text.validation.cancelSemester}
            </Col>
          )}

          {selectedType && selectedType.paymentRequired && (
            <Col md={6}>
              <TextInput
                label={text.payment}
                name="payment"
                formatter={TariffFormatter}
                control={control}
                disabled={true}
              />
            </Col>
          )}

          {selectedType &&
            (selectedType.value === PetitionTypes.ACADEMIC ||
              selectedType.value === PetitionTypes.PAYMENT ||
              selectedType.value === PetitionTypes.OTHER) && (
              <PetitionFormDefault
                careerOptions={careerOptions}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.REPLACE_EVALUATION_GRADE && (
              <PetitionFormReplaceEvaluationGrade
                careerOptions={careerOptions}
                replaceEvaluationGrades={replaceEvaluationGrades}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.REPLACE_COURSE_GRADE && (
              <PetitionFormReplaceCourseGrade
                careerOptions={careerOptions}
                sectionData={replaceCourseGrades}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType && selectedType.value === PetitionTypes.PROFICIENCY && (
            <PetitionFormProficiency
              careerOptions={careerOptions}
              proficiencyCourses={proficiencyCourses}
              onLoadingChange={handleLoadingChange}
            />
          )}

          {selectedType && selectedType.value === PetitionTypes.VALIDATION && (
            <PetitionFormValidation
              careerOptions={careerOptions}
              // se usan los mismos datos que los de proficiency
              validationCourses={proficiencyCourses}
              onLoadingChange={handleLoadingChange}
            />
          )}

          {selectedType &&
            selectedType.value === PetitionTypes.CANCEL_COURSE && (
              <PetitionFormCancelCourse
                careerOptions={careerOptions}
                activeSections={activeSections || []}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.CANCEL_SEMESTER && (
              <PetitionFormCancelSemester
                careerOptions={careerOptions}
                periodName={periodName}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.FREEZE_SEMESTER && (
              <PetitionFormFreezeSemester
                careerOptions={careerOptions}
                periodName={periodName}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.FREEZE_CREDITS && (
              <PetitionFormFreezeCredits
                careerOptions={careerOptions}
                maxCreditsToFreeze={maxCreditsToFreeze}
                periodName={periodName}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.SUBJECT_ASSIGNMENT && (
              <PetitionFormSubjectAssignment
                careerOptions={careerOptions}
                subjectAssignmentCourses={subjectAssignmentCourses}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType && selectedType.value === PetitionTypes.REINTEGRO && (
            <PetitionFormReintegro
              programOptions={reintegroProgramOptions}
              onLoadingChange={handleLoadingChange}
            />
          )}

          <Col md={6}>
            <Button
              type="button"
              className="w-100"
              outlined
              loading={loadingSave}
              onClick={() => {
                close();
                clearForm();
              }}
              text={text.cancel}
            />
          </Col>
          <Col md={6}>
            <Button
              type="submit"
              className="w-100"
              color="primary"
              loading={loadingSave}
              disabled={
                isFileLoading ||
                showCancellationCourseWarning ||
                showCancellationSemesterWarning
              }
              text={text.save}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}
